import { Seo } from "@/components/Seo";

const H2 = ({ children }) => <h2 className="font-heading font-black text-xl sm:text-2xl tracking-tighter mt-10 mb-3 uppercase">{children}</h2>;
const P = ({ children }) => <p className="font-mono text-sm leading-relaxed text-ink/90 mb-3">{children}</p>;
const UL = ({ children }) => <ul className="font-mono text-sm leading-relaxed text-ink/90 mb-3 list-disc pl-6 space-y-1">{children}</ul>;

export default function Privacy() {
  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-8 py-10" data-testid="privacy-page">
      <Seo title="Privacy Policy · STR Surplus" description="How STR Surplus LLC collects, uses, and protects your personal information." canonical="/privacy" />
      <div className="border-b-2 border-ink pb-4 mb-6">
        <div className="font-mono text-[11px] uppercase tracking-widest text-subtle">Legal</div>
        <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tighter mt-1">PRIVACY POLICY</h1>
        <div className="font-mono text-xs text-subtle mt-2">Last updated: February 6, 2026 · STR Surplus LLC · Fort Lauderdale, FL 33304</div>
      </div>

      <P><strong>STR Surplus LLC</strong> ("we", "us", "our") operates <a href="/" className="underline">strsurplus.com</a>. This Privacy Policy explains what personal information we collect, how we use it, how we share it, and the choices you have.</P>
      <P>By using the site or placing an order, you agree to this policy. If you do not agree, please do not use the site.</P>

      <H2>1. Information we collect</H2>
      <P>We collect only the data needed to fulfill orders, run the site, and communicate with buyers:</P>
      <UL>
        <li><strong>Contact info</strong> — name and email address you provide at checkout or on the contact form.</li>
        <li><strong>Shipping info</strong> — mailing address, city, state, ZIP, and phone number (optional).</li>
        <li><strong>Payment info</strong> — collected and processed <em>directly by Stripe</em>. We never see, store, or transmit full credit-card numbers. We store only Stripe's transaction ID and the last four digits of the card for order records.</li>
        <li><strong>Account info</strong> — if you register, we store your email and a hashed (bcrypt) password. We cannot recover a lost password; you must reset it.</li>
        <li><strong>Order history</strong> — line items you purchased, prices, dates, shipping status, tracking numbers.</li>
        <li><strong>Analytics &amp; advertising cookies</strong> — set by our <strong>analytics partners</strong> to record pages viewed, products viewed, items added to cart, and purchases. These are set only if you accept our cookie banner (see §5).</li>
        <li><strong>Device data</strong> — IP address, browser type, and OS. Used for fraud prevention and site-performance monitoring.</li>
      </UL>

      <H2>2. How we use your information</H2>
      <UL>
        <li>To process and fulfill your orders (packing, shipping, tracking).</li>
        <li>To send transactional email: order confirmation, shipping notice, review request, cart-abandonment reminder.</li>
        <li>To provide customer service and answer replies to <a href="mailto:orders@strsurplus.com" className="underline">orders@strsurplus.com</a>.</li>
        <li>To improve the site — anonymous browsing analytics, best-seller detection.</li>
        <li>To detect and prevent fraud.</li>
        <li>If you have accepted marketing cookies, to show you relevant ads on Facebook, Instagram, and other Meta properties.</li>
      </UL>

      <H2>3. Third parties we share with</H2>
      <P>We only share the minimum data each partner needs to do their job:</P>
      <UL>
        <li><strong>Stripe</strong> — payment processing. Governed by <a href="https://stripe.com/privacy" className="underline" target="_blank" rel="noopener noreferrer">Stripe's Privacy Policy</a>.</li>
        <li><strong>UPS</strong> — shipping (name and address only).</li>
        <li><strong>Resend / DreamHost</strong> — transactional and reply-to email delivery.</li>
        <li><strong>Analytics partners</strong> — including <strong>Meta (Facebook / Instagram)</strong> via its pixel — but only if you accept marketing cookies. Governed by <a href="https://www.facebook.com/privacy/policy" className="underline" target="_blank" rel="noopener noreferrer">Meta's Privacy Policy</a>.</li>
        <li><strong>Emergent</strong> — our web hosting provider.</li>
        <li><strong>Law enforcement</strong> — if compelled by a valid subpoena, court order, or fraud investigation.</li>
      </UL>
      <P>We do not sell, rent, or trade your personal information. Ever.</P>

      <H2>4. Data retention</H2>
      <UL>
        <li>Order records: kept 7 years for tax and warranty compliance.</li>
        <li>Account data: kept until you request deletion.</li>
        <li>Analytics cookies: kept up to 24 months (Meta Pixel default).</li>
        <li>Cart-abandonment records: kept 90 days after checkout, then anonymized.</li>
      </UL>

      <H2>5. Cookies &amp; tracking</H2>
      <P>The site uses two categories of cookies:</P>
      <UL>
        <li><strong>Strictly necessary</strong> — session, cart, login (cannot be disabled).</li>
        <li><strong>Analytics &amp; marketing</strong> — set by our analytics partners (including Meta / Facebook, Pixel ID 680716584907420). These do <em>not</em> load until you click <strong>Accept</strong> on the cookie banner. You can withdraw consent at any time via the <strong>Cookie Preferences</strong> link in the footer.</li>
      </UL>

      <H2>6. Your rights</H2>
      <P>Regardless of where you live, you may request:</P>
      <UL>
        <li>A copy of the personal data we hold about you.</li>
        <li>Correction of inaccurate data.</li>
        <li>Deletion of your account and associated data (subject to legal record-keeping requirements above).</li>
        <li>To opt out of marketing cookies at any time (Cookie Preferences in footer).</li>
        <li>To unsubscribe from marketing email via the footer link on any email we send.</li>
      </UL>
      <P>California residents have additional rights under the <strong>CCPA</strong>, including the right to know what personal information we collect and the right to opt-out of the "sale" of personal information (we do not sell personal information).</P>
      <P>To exercise any right, email <a href="mailto:orders@strsurplus.com" className="underline">orders@strsurplus.com</a> with the subject "Privacy Request". We respond within 30 days.</P>

      <H2>7. Data security</H2>
      <P>The site uses HTTPS encryption for all traffic. Passwords are stored using bcrypt hashing. Payments run through Stripe's PCI-DSS Level-1 environment. We audit third-party access quarterly.</P>
      <P>No online service is 100% secure. If we ever become aware of a data breach affecting your information, we will notify you within 72 hours.</P>

      <H2>8. Children</H2>
      <P>STR Surplus is not directed to children under 16. We do not knowingly collect data from anyone under 16. If you believe we have, contact us and we will delete it.</P>

      <H2>9. Changes</H2>
      <P>We may update this Privacy Policy from time to time. The "Last updated" date at the top of the page reflects the latest change. Material changes will also be announced at the top of the homepage for at least 14 days.</P>

      <H2>10. Contact</H2>
      <P>Questions or requests about this Privacy Policy:</P>
      <P>
        STR Surplus LLC<br />
        Fort Lauderdale, FL 33304<br />
        FL Sunbiz Doc No. L26000449383<br />
        <a href="mailto:orders@strsurplus.com" className="underline">orders@strsurplus.com</a>
      </P>
    </div>
  );
}
